import axios, {
  AxiosError,
  AxiosRequestConfig,
  CanceledError,
  Method,
} from "axios";
import Cookie from "./cookie";
import getErrorMessage from "@/modules/getErrorMessage";
import { useGlobalStream } from "@/stores/globalStream";
import { useGlobalUser } from "@/stores/globalUser";
import { RouteLocationNormalized, Router } from "vue-router";
import { Response } from "@/types/api-types";
import { showNotify } from "vant";
import { parseObject } from "@/modules/common";
import i18n from "@/locales";
import { StorageGet } from "@/tools/localStorage";
import {
  sessionStorageGet,
  sessionStorageRemove,
} from "@/tools/sessionStorage";
import { useThrottleFn } from "@vueuse/core";
import { useGlobalModal } from "@/stores/globalModal";

export const API_PREFIX = "/v1/Api";

const alreadyHandlerErrorMsg = new Set<string>([
  "Difficulty level is required when ranking is enabled.",
  "任务目标已变更，请重新评估任务难度",
  "The objective has changed. Please re-evaluate its difficulty level",
  "当 difficulty level 存在时 objective signature 不能为空。",
  "最多只能同时发布 3 条领养信息",
  "You can only post up to 3 adoption posts",
]);

const API_URL = import.meta.env.DEV
  ? API_PREFIX
  : import.meta.env.VITE_API_URL + API_PREFIX;

export const $http = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

function supplyHeader(headers: Record<string, any>) {
  const globalUser = useGlobalUser();
  const mateId =
    globalUser.scopedCurrentMateId ||
    globalUser.currentMateId ||
    sessionStorageGet("user").mateId;
  if (mateId) {
    headers["SwitchMateId"] = mateId;
  }
}

$http.interceptors.request.use((config) => {
  if (!config.headers["X-XSRF-TOKEN"])
    config.headers["X-XSRF-TOKEN"] =
      decodeURIComponent(Cookie.get("XSRF-TOKEN")!) || "undefined";
  const globalUser = useGlobalUser();
  const mateId =
    globalUser.scopedCurrentMateId ||
    globalUser.currentMateId ||
    sessionStorageGet("user")?.mateId;
  if (mateId) {
    config.headers["SwitchMateId"] = mateId;
  }
  return config;
});

const refreshTokenAndRetry = async (error: any) => {
  const globalUser = useGlobalUser();
  await globalUser.refresh();
  return axios.request(error.config);
};

const checkIfSwitchAccount = useThrottleFn(
  (error: AxiosError<Response<void>>) => {
    const globalUser = useGlobalUser();
    const userInLocalStorage = StorageGet("user");
    if (
      globalUser.id &&
      StorageGet("user") &&
      userInLocalStorage.id !== globalUser.id
    ) {
      // 1. 在其他窗口登录了另一个账号 => 清除session缓存，刷新页面
      showNotify({
        message: i18n.global.t("Message.switchingAccount"),
        type: "primary",
      });
      sessionStorageRemove("user");
      location.reload();
    } else {
      // 2. 无权限 => 显示错误信息
      const errorMsg = error!.response?.data?.message ?? error.message;
      showNotify({ message: errorMsg, type: "danger" });
    }
  },
  3000
);

/**
 * 请求拦截器
 * 方法内部需要使用router等vue实例，所以需要在vue实例创建后调用
 */
export function registerInterceptors(
  route: RouteLocationNormalized,
  router: Router
) {
  $http.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error instanceof CanceledError) {
        throw error;
      }

      if (!error.response || !error.response.status) {
        showNotify(error.message);
        throw error;
      }
      const { status, data } = error.response;
      if (status === 401) {
        // 1. 未登录 => 跳转到登录页
        const globalUser = useGlobalUser();
        if (globalUser.isLogin) {
          globalUser.logout(false);
        }
        if (
          route.matched.some((record) => record.meta.auth) &&
          route.name !== "login"
        ) {
          globalUser.loginModal.show = true;
        }
        if (error.config.url === "/UserHome") return;
      } else if (status === 419) {
        // Token过期
        return refreshTokenAndRetry(error);
      } else if (status === 403) {
        // 3. 无权限(可能是在其他窗口登录了另一个账号)
        if (data.code === "SUBSCRIPTION_REQUIRED") {
          useGlobalModal().showSubscribeModal = true;
        } else {
          checkIfSwitchAccount(error);
        }
      }

      //通知消息
      const { message, details } = data;
      if (data && data.message) {
        if (message.includes("Azure NSFW")) {
          showNotify({
            type: "danger",
            message: i18n.global.t("Message.azureError"),
          });
        } else if (["水晶不足", "Insufficient crystals"].includes(message)) {
          await useGlobalUser().refresh();
          useGlobalModal().showCrystalNotEnoughModal = true;
        } else if (
          ["能量块不足", "Insufficient power cubes"].includes(message)
        ) {
          const userStore = useGlobalUser();
          await userStore.refresh();
          const modalStore = useGlobalModal();
          if (details?.missingAmount) {
            modalStore.spentPowerAmount =
              userStore.user.powerAmount + details.missingAmount;
            modalStore.showPowerNotEnoughModal = true;
          }
        } else {
          showNotify({ message, type: "danger" });
        }
      } else if (!alreadyHandlerErrorMsg.has(message)) {
        showNotify({
          message: i18n.global.t("Message.connectionFaild"),
          type: "danger",
        });
      }
      throw error;
    }
  );
}

export const get = async (url: string, params = {}) => {
  const accessToken = Cookie.get("anymate_session") ?? "";
  const res = await $http.get(url, {
    params,
    headers: { authorization: accessToken },
  });
  return res?.data;
};

export const post = async (
  url: string,
  params = {},
  config: AxiosRequestConfig<any> = {}
) => {
  const res = await $http.post(url, params, config);
  return res.data;
};
export const put = async (
  url: string,
  params = {},
  config: AxiosRequestConfig<any> = {}
) => {
  const res = await $http.put(url, params, config);
  return res.data;
};

export async function deleteOne(uri: string) {
  const res = await $http.delete(uri);
  return res.data;
}

export const sendBlob = async (blob: Blob) => {
  const formData = new FormData();
  formData.append("source", blob);
  const response = await $http.post("/Upload/0", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const processStreamFinalResponse = async (
  jsonStr: string,
  response: globalThis.Response,
  callback: (content: Response<any> | string) => void | Promise<void>
) => {
  const json = parseObject(jsonStr);
  if (response.ok && json) {
    await callback(json);
  } else {
    const res = json || (await response.json().catch(() => null));
    if (!res) {
      return;
    }
    if (
      res.message === "Failed to get chat answer" &&
      response.status === 503
    ) {
      useGlobalModal().openServerUpgradingModal(true);
      throw new Error(res.message);
    } else if (res.code === "SUBSCRIPTION_REQUIRED") {
      useGlobalModal().showSubscribeModal = true;
      throw new Error(res.message);
    } else {
      const errorMsg = getErrorMessage(res);
      showNotify({
        message: errorMsg,
        type: "danger",
      });
      throw new Error(errorMsg);
    }
  }
};

function containsJsonFragment(text: string) {
  // 使用正则表达式查找可能的 JSON 片段
  const jsonLikePatterns = text.match(/\{".*?":.*?}/g);
  return !!jsonLikePatterns;
}

export async function fetchStream<T>(
  url: string,
  method: Method,
  body = {},
  callBack: (content: Response<T> | string) => void | Promise<void>,
  header = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "X-XSRF-TOKEN": decodeURIComponent(Cookie.get("XSRF-TOKEN")!),
  }
) {
  const globalStream = useGlobalStream();
  supplyHeader(header);
  const response = await fetch(API_URL + url, {
    method,
    body: JSON.stringify(body),
    credentials: "include",
    headers: header,
    signal: globalStream.controller.signal,
  });
  if (response.status !== 200) {
    processStreamFinalResponse("error", response, callBack);
    return;
  }

  const td = new TextDecoder();
  const reader = response.body!.getReader();
  const jsonBuffer: string[] = [];
  let isDone = false;
  while (true) {
    try {
      const { done, value } = await reader.read();
      if (done) {
        await processStreamFinalResponse(
          jsonBuffer.join(""),
          response,
          callBack
        );
        break;
      }
      const text = td.decode(value);

      if (!text.includes("<Done />")) {
        if (!isDone) {
          callBack(text);
        } else {
          jsonBuffer.push(text);
        }
      } else {
        isDone = true;
        const [txt, json] = text.split("<Done />");
        if (txt) {
          callBack(txt);
        }
        jsonBuffer.push(json);
      }
    } catch {
      await processStreamFinalResponse(jsonBuffer.join(""), response, callBack);
      break;
    }
  }
}
